/* eslint-disable no-param-reassign */
import {
  BaseExportParams,
  CellSelectionOptions,
  CellValueChangedEvent,
  ColDef,
  FillOperationParams
} from 'ag-grid-enterprise'
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { Dayjs } from 'dayjs'
import { mean } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AgGridReact } from 'ag-grid-react'
import { UndoOutlined, RedoOutlined } from '@ant-design/icons'
import { Space, Button } from 'antd'
import { formatAsDate, createDate } from '../../../config/dayjsConfig'
import { StatementType, reportNames } from '../types/exchangeRate'
import { CustomReportDataType } from '../../../redux/context/customReports/typesVariable'
import { IndexedDefaultExchangeRates } from '../queries/defaultExchangeRateOptions'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import {
  formatNumber,
  getLocaleFromLanguage,
  parseLocalizedNumber
} from '../../../shared/components/custom/Grid/gridFormatUtils'
import { appLanguageSelector } from '../../../redux/app/selectors'
import { ModifiedCustomExchangeRate } from '../hooks/useExchangeRateReducer'
import ToolBar from '../../../components/Misc/ToolBar'

interface GridRowData {
  statementType: StatementType
  dataType: keyof typeof CustomReportDataType
  [key: string]: string | number | null // dynaamisia kuukausi-kenttiä varten
}

interface ExchangeRateGridProps {
  data: GridRowData[] // Row data tulee nyt propsina
  defaultRates?: IndexedDefaultExchangeRates
  dates: Dayjs[]
  onChange: (rate: ModifiedCustomExchangeRate) => void
  resetKey?: number
  defaultExportParams?: BaseExportParams
}

const ExchangeRateGrid = forwardRef<AgGridReact, ExchangeRateGridProps>(
  ({ data, defaultRates, dates, resetKey, onChange, defaultExportParams }, ref) => {
    const { t } = useTranslation()
    const language = useSelector(appLanguageSelector)
    const locale = getLocaleFromLanguage(language)
    const [rowData, setRowData] = useState<GridRowData[]>(data)

    useEffect(() => {
      setRowData(data)
    }, [resetKey])

    const handleUndo = useCallback(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.api.undoCellEditing()
      }
    }, [ref])

    const handleRedo = useCallback(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.api.redoCellEditing()
      }
    }, [ref])

    const onCellValueChanged = useCallback(
      (event: CellValueChangedEvent<GridRowData>): void => {
        const { colDef, data: rData } = event
        const { field } = colDef

        if (!field || !rData) return

        const { statementType, dataType } = rData

        if (field === 'statementType' || field === 'dataType' || !field.includes('/')) {
          return
        }

        const value = rData[field]

        // Parse MM/YYYY format (e.g., "03/2024")
        const [monthStr, yearStr] = field.split('/')
        const month = parseInt(monthStr, 10) // 1-12 month
        const year = parseInt(yearStr, 10) // 4-digit year
        const date = createDate(year, month, 1)
        const dateString = formatAsDate(date) || ''

        const newRate: ModifiedCustomExchangeRate = {
          statementType,
          dataType: CustomReportDataType[dataType],
          month,
          year,
          value,
          date: dateString
        }

        onChange(newRate)
      },
      [onChange]
    )

    // Kolumnit - sama logiikka
    const colDefs = useMemo(() => {
      const columns: ColDef<GridRowData>[] = [
        {
          field: 'statementType',
          headerName: t('global:report'),
          width: 140,
          pinned: 'left',
          editable: false,
          valueFormatter: params => {
            const reportKey = reportNames[params.value as StatementType]
            return t(`reportTypes:${reportKey}`)
          },
          cellStyle: { fontWeight: 'bold' }
        },
        {
          field: 'dataType',
          headerName: t('global:dataType'),
          width: 110,
          pinned: 'left',
          editable: false,
          valueFormatter: params => {
            return t(`global:${params.value}`)
          },
          cellStyle: { fontWeight: '500' }
        }
      ]
      // Kuukausi-kolumnit
      dates.forEach(date => {
        const monthKey = date.format('MM/YYYY')
        const mKey = date.format('MM/YYYY')
        columns.push({
          field: monthKey,
          headerName: date.format('MM/YYYY'),
          width: 120,
          type: 'numeric',
          editable: true,
          // Käytä valueGetter:ia default arvojen käsittelyyn
          valueSetter: params => {
            const { newValue, colDef } = params

            if (newValue === null || newValue === '' || newValue === undefined) {
              params.data[colDef.field!] = null
              return true
            }

            const parsed = parseLocalizedNumber(newValue, locale)
            if (parsed === null) {
              return false
            }

            params.data[colDef.field!] = parsed
            return true
          },

          valueGetter: params => {
            const raw = params.data?.[monthKey]
            if (raw == null) return null
            return typeof raw === 'string' ? parseFloat(raw) || null : raw
          },

          valueFormatter: params => {
            const value = params.value ?? defaultRates?.[`${params.data?.statementType}-${mKey}`]?.value ?? null
            if (value == null) return ''
            return formatNumber(value, locale, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 4
            })
          },
          cellStyle: params => {
            // Check the actual field value (not valueGetter result) to style default rate cells
            const hasOwnValue = params.data?.[monthKey] !== null && params.data?.[monthKey] !== undefined

            return {
              backgroundColor: hasOwnValue ? 'inherit' : '#ececec',
              color: hasOwnValue ? 'inherit' : '#999999',
              fontStyle: hasOwnValue ? 'normal' : 'italic',
              cursor: 'pointer'
            }
          }
        })
      })

      // Keskiarvo
      columns.push({
        headerName: t('exchangeRatesPage:averageRate'),
        field: 'average',
        width: 100,
        pinned: 'right',
        type: 'numericColumn',
        editable: false,
        valueGetter: params => {
          const values = dates
            .map(date => {
              const mKey = date.format('MM/YYYY')
              const raw = params.data?.[mKey]
              const val = raw ?? defaultRates?.[`${params.data?.statementType}-${mKey}`]?.value ?? null
              return typeof val === 'string' ? parseFloat(val) : val
            })
            .filter((val): val is number => typeof val === 'number' && Number.isFinite(val))
          return values.length > 0 ? mean(values) : null
        },
        valueFormatter: params =>
          formatNumber(params.value, locale, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          }),
        cellStyle: {
          fontWeight: 'bold',
          backgroundColor: '#f0f8ff'
        }
      })

      return columns
    }, [dates, defaultRates, locale, t])

    const defaultColDef = useMemo<ColDef>(() => {
      return {
        sortable: true,
        filter: false,
        resizable: true,
        editable: true,
        enableCellChangeFlash: true
      }
    }, [])

    const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
      return {
        handle: {
          mode: 'fill',
          setFillValue: (params: FillOperationParams) => {
            return params.initialValues[params.currentIndex % params.initialValues.length]
          }
        }
      }
    }, [])

    return (
      <div style={{ width: '100%' }}>
        <ToolBar justifyContent="left">
          <Space>
            <Button icon={<UndoOutlined />} onClick={handleUndo} size="small" title="Undo (Ctrl+Z)">
              {t('actions:undo')}
            </Button>
            <Button icon={<RedoOutlined />} onClick={handleRedo} size="small" title="Redo (Ctrl+Y)">
              {t('actions:redo')}
            </Button>
          </Space>
        </ToolBar>
        <CustomAgGrid
          domLayout="autoHeight"
          ref={ref}
          getRowId={params => `${params.data.statementType}-${params.data.dataType}`}
          rowData={rowData}
          columnDefs={colDefs}
          onCellValueChanged={onCellValueChanged}
          defaultColDef={defaultColDef}
          cellSelection={cellSelection}
          undoRedoCellEditing
          undoRedoCellEditingLimit={20}
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          defaultExcelExportParams={defaultExportParams}
          defaultCsvExportParams={defaultExportParams}
        />
      </div>
    )
  }
)

export default ExchangeRateGrid
